import { CommonModule } from '@angular/common';
import {
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatRippleModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { AnalyticsModule } from '../analytics/analytics.module';
import { GoogleModule } from '../google/google.module';
import { AppNotificationComponent } from './components/app-notification/app-notification.component';
import { ConfigurationQuestionComponent } from './components/configuration-question/configuration-question.component';
import { CookiePolicyComponent } from './components/cookie-policy/cookie-policy.component';
import { InlineEditorComponent } from './components/inline-editor/inline-editor.component';
import { LoadingAnimationComponent } from './components/loading-animation/loading-animation.component';
import { ThemeColorSwatchesComponent } from './components/theme-color-swatches/theme-color-swatches.component';
import { EcapsCore } from './controllers/ecaps-core.controller';
import { LoadingGraphic } from './controllers/loading-graphic.controller';
import { RepLocatorDialog } from './controllers/rep-locator-dialog.controller';
import { BathSpCalculatorDialogComponent } from './dialogs/bath-sp-calculator-dialog/bath-sp-calculator-dialog.component';
import { ElevationDialogComponent } from './dialogs/elevation-dialog/elevation-dialog.component';
import { EmailDialogComponent } from './dialogs/email-dialog/email-dialog.component';
import { ErrorMessageDialogComponent } from './dialogs/error-message-dialog/error-message-dialog.component';
import { ErrorReportingDialogComponent } from './dialogs/error-reporting-dialog/error-reporting-dialog.component';
import { HelpItemDialogComponent } from './dialogs/help-item-dialog/help-item-dialog.component';
import { MessageBoxDialogComponent } from './dialogs/message-box-dialog/message-box-dialog.component';
import { RepLocatorDialogComponent } from './dialogs/rep-locator-dialog/rep-locator-dialog.component';
import { GreenheckSpecialCharactersDirective } from './directives/greenheck-special-characters.directive';
import { InputUppercaseDirective } from './directives/input-uppercase.directive';
import { LoadChildDirective } from './directives/load-child.directive';
import { OverflowTooltipDirective } from './directives/overflow-tooltip.directive';
import { SquareDirective } from './directives/square.directive';
import { StopPropClickDirective } from './directives/stop-prop-click.directive';
import { VerticalWrapDirective } from './directives/vertical-wrap.directive';
import {
  AttributeFilterPipe,
  BoolToYesNoPipe,
  BreakHorsePowerPipe,
  CamelCasePipe,
  CamelToDisplayPipe,
  DisplayMaskPipe,
  EmptyStringToDashPipe,
  FieldErrorToTextPipe,
  FormatObjectPipe,
  FractionPipe,
  HemisphericalSonesPipe,
  MaxThroatVelocityPipe,
  PercentZeroToDash,
  SafeHTMLPipe,
  SafeURLPipe,
  SphericalSonesPipe,
  ZeroToDashPipe,
} from './pipes/custom-pipes.pipe';
import { CookiePolicyService } from './services/cookie-policy.service';
import { EmailService } from './services/email.service';
import { HttpService } from './services/http.service';
import {
  SideDialogActionsDirective,
  SideDialogCloseDirective,
  SideDialogContentDirective,
  SideDialogTitleDirective,
} from './side-dialog/side-dialog.directives';
import { SideDialogService } from './side-dialog/side-dialog.service';

@NgModule({
  declarations: [
    AttributeFilterPipe,
    CamelCasePipe,
    FractionPipe,
    HemisphericalSonesPipe,
    SphericalSonesPipe,
    BreakHorsePowerPipe,
    ZeroToDashPipe,
    EmptyStringToDashPipe,
    CamelToDisplayPipe,
    PercentZeroToDash,
    BoolToYesNoPipe,
    DisplayMaskPipe,
    FormatObjectPipe,
    MaxThroatVelocityPipe,
    FieldErrorToTextPipe,
    SafeHTMLPipe,
    SafeURLPipe,
    ConfigurationQuestionComponent,
    InlineEditorComponent,
    ElevationDialogComponent,
    MessageBoxDialogComponent,
    LoadChildDirective,
    ErrorMessageDialogComponent,
    EmailDialogComponent,
    RepLocatorDialogComponent,
    GreenheckSpecialCharactersDirective,
    InputUppercaseDirective,
    ErrorReportingDialogComponent,
    BathSpCalculatorDialogComponent,
    HelpItemDialogComponent,
    StopPropClickDirective,
    AppNotificationComponent,
    CookiePolicyComponent,
    SquareDirective,
    ThemeColorSwatchesComponent,
    OverflowTooltipDirective,
    VerticalWrapDirective,
    LoadingAnimationComponent,
  ],
  exports: [
    AttributeFilterPipe,
    CamelCasePipe,
    FractionPipe,
    HemisphericalSonesPipe,
    SphericalSonesPipe,
    BreakHorsePowerPipe,
    ZeroToDashPipe,
    EmptyStringToDashPipe,
    CamelToDisplayPipe,
    PercentZeroToDash,
    BoolToYesNoPipe,
    DisplayMaskPipe,
    FormatObjectPipe,
    MaxThroatVelocityPipe,
    FieldErrorToTextPipe,
    SafeHTMLPipe,
    SafeURLPipe,
    ConfigurationQuestionComponent,
    InlineEditorComponent,
    LoadChildDirective,
    GreenheckSpecialCharactersDirective,
    InputUppercaseDirective,
    StopPropClickDirective,
    FieldErrorToTextPipe,
    AppNotificationComponent,
    CookiePolicyComponent,
    SquareDirective,
    ThemeColorSwatchesComponent,
    OverflowTooltipDirective,
    VerticalWrapDirective,
    SideDialogTitleDirective,
    SideDialogContentDirective,
    SideDialogActionsDirective,
    SideDialogCloseDirective,
  ],
  imports: [
    CommonModule,
    MatDialogModule,
    FormsModule,
    MatIconModule,
    GoogleModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatTooltipModule,
    MatToolbarModule,
    MatBadgeModule,
    MatMenuModule,
    MatCardModule,
    MatStepperModule,
    MatDividerModule,
    AnalyticsModule,
    RouterModule,
    MatExpansionModule,
    MatSlideToggleModule,
    MatRippleModule,
    SideDialogTitleDirective,
    SideDialogContentDirective,
    SideDialogActionsDirective,
    SideDialogCloseDirective,
  ],
  providers: [
    EcapsCore,
    LoadingGraphic,
    EmailService,
    RepLocatorDialog,
    HttpService,
    CookiePolicyService,
    SideDialogService,
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class EcapsCoreModule {
  constructor(
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer
  ) {
    this.loadSvgIcons();
  }

  private loadSvgIcons(): void {
    const icons: Record<string, string> = {
      jobs: '/assets/images/icons/svg/jobs.svg',
      save_as: '/assets/images/icons/svg/saveas.svg',
      ses: '/assets/images/icons/svg/ses.svg',
      submittal: '/assets/images/icons/svg/submittal.svg',
      droplet: '/assets/images/icons/svg/droplet.svg',
      chart: '/assets/images/icons/svg/chart.svg',
      er_wheel: '/assets/images/icons/svg/er_wheel.svg',
      drawing: '/assets/images/icons/svg/drawing.svg',
      dimensions: '/assets/images/icons/svg/dimensions.svg',
      amca_icon: '/assets/images/icons/svg/amca_icon.svg',
      down_arrow: '/assets/images/icons/svg/down_arrow.svg',
      ecaps_logo: '/assets/images/icons/svg/ecaps_logo.svg',
      hurricane_testing: '/assets/images/icons/svg/hurricane_testing.svg',
      sure_aire: '/assets/images/icons/svg/sure_aire.svg',
      fan: '/assets/images/icons/svg/fan.svg',
      dash: '/assets/images/icons/svg/dash.svg',
      induction: '/assets/images/icons/svg/induction.svg',
      greenheck: '/assets/images/icons/svg/greenheck.svg',
      greenheck_font: '/assets/images/icons/svg/greenheck_font.svg',
      revit: '/assets/images/icons/svg/revit.svg',
      pdf: '/assets/images/icons/svg/pdf.svg',
      exporta: '/assets/images/icons/svg/exporta.svg',
      exportb: '/assets/images/icons/svg/exportb.svg',
      calculator: '/assets/images/icons/svg/calculator.svg',
      calculator_fx: '/assets/images/icons/svg/calculator_fx.svg',
      calculator_sum: '/assets/images/icons/svg/calculator_sum.svg',
      mfan: '/assets/images/icons/svg/mfan.svg',
      excel: '/assets/images/icons/svg/excel.svg',
    };

    Object.keys(icons).forEach((iconName) => {
      this.iconRegistry.addSvgIcon(
        iconName,
        this.sanitizer.bypassSecurityTrustResourceUrl(icons[iconName])
      );
    });
  }
}
